<template>
  <div>
    <Lightbox :images="lightboxImages"
              :selected="lightboxSelected"
              v-if="lightboxVisible"
              @close="closeLightbox" />
    <div id="app" ref="app" @resize="onAppResize" :class="{'app-blur': appBlur}">
      <transition-group appear style="position: relative; margin: 0 auto; max-width: 900px;"
                        name="flakes" tag="div"
                        v-on:before-enter="beforeEnter" v-on:enter="enter"
                        :css="false">
        <span v-if="show" v-for="item in [1,2,3,4,5]" :key="item" :data-index="item" />
      </transition-group>
      <NavHeader />
      <main>
        <router-view/>
      </main>
      <LinksFooter />
    </div>
    <div :class="[{'app-blur': appBlur}, 'to-top']">
      <sb-button v-scroll-to="'#app'" class="top-link with-shadow">
        <font-awesome-icon icon="chevron-up" />
      </sb-button>
    </div>
  </div>
</template>

<script>
import Velocity from 'velocity-animate';
import NavHeader from './layout/NavHeader.vue';
import LinksFooter from './layout/LinksFooter.vue';
import SbButton from './shared/SbButton.vue';
import Lightbox from './shared/Lightbox.vue';
import store from "./store";

export default {
  name: 'App',
  components: {
    Lightbox, NavHeader, LinksFooter, SbButton,
  },
  data: () => ({
    show: true,
  }),
  computed: {
    lightboxVisible() {
      return this.$store.state.lightboxVisible;
    },
    lightboxImages() {
      return this.$store.state.lightboxImages;
    },
    appBlur() {
      return this.$store.state.appBlur;
    },
    lightboxSelected() {
      return this.$store.state.lightboxSelected;
    },
  },
  methods: {
    closeLightbox() {
      this.$store.commit('hideLightbox');
    },
    beforeEnter(el) {
      el.style.position = 'absolute';
      el.style.background = 'url("/blossom.png")';
      el.style.backgroundSize = 'cover';
      el.style.opacity = 0;
      el.style.zIndex = '4';
    },
    enter(el, done) {
      const vm = this;
      setInterval(function runBlossomAnimation() {
        const { width, height } = vm.$refs.app.getBoundingClientRect();
        const [rndSize, top, left, delay] = [
          Math.floor((Math.random() * 10) + 15 + 1),
          Math.floor((Math.random() * 130) + 1),
          Math.floor((Math.random() * width) - 20 + 1),
          Math.floor((Math.random() * (10000 / Number.parseInt(el.dataset.index) * 2)) + 1),
        ];
        Velocity(
          el,
          {
            position: 'absolute',
            width: rndSize,
            height: rndSize,
            top,
            left,
          },
        );
        Velocity(
          el,
          {
            opacity: 0.8,
          },
          {
            delay,
          },
        );
        Velocity(
          el,
          {
            top: top + 500,
            left: left + 50,
            opacity: 0,
            rotateZ: '+=-270',
          },
          {
            duration: 10000,
            complete: done,
          },
        );
      }, 100);
    },
    onAppResize() {
      Velocity('stop');
      this.show = false;
      setTimeout(function hide() { this.show = true; }, 7000);
    },
  },
  metaInfo() {
    return {
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
      title: 'Shogi',
      titleTemplate: '%s | Shogi Berlin',
    };
  },
};

document.showLightbox = function (el) {
  const imgData = Array.from(el.parentNode.children).map(img => ({ src: img.src, alt: img.alt }));
  const selected = imgData.findIndex(img => img.src === el.src);
  store.commit('lightboxImages', imgData.map(img => img.src.replace(/_[wh][0-9]{3}/, '')));
  store.commit('setLightboxSelected', selected);
  store.commit('showLightbox');
};
</script>

<style>
:root {
  --bg-soft: #fff2f8;
  --bg-hard: #eaa4c8;
  --link-color: #454E9E;
  --coral: #ff7f50;
  --coral-o-cc: #ff7f50cc;
  --reddish: #FA2D36;
  --pinkish: #ff1d5e;
}

.app-blur {
  filter: blur(15px);
}

.to-top {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.top-link {
  margin: 0.5rem;
}

.with-shadow {
  box-shadow: 0 5px 5px 0 #00000040;
}

body {
  cursor: url(~/shogi-piece.cur), auto;

  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 0.95rem;
  line-height: 1.5rem;
  background: var(--bg-soft);
  background: linear-gradient(to bottom, var(--bg-soft) 0%, var(--bg-hard) 100%);
  background-repeat: no-repeat;
  background-attachment: fixed;
  margin: 0;
  padding: 0;
}

#app {
  margin: 10px auto;
  box-shadow: 0 5px 5px #00000040;
  border-radius: 5px;
  max-width: 900px;
  /*overflow: hidden;*/
}

main {
  position: relative;
  display: block;
  z-index: 3;
  background-color: white;
  width: 100%;
  height: 100%;
  box-shadow: 0 5px 5px 0 rgba(0, 0, 0, 0.08);
}

article {
  padding: 4% 5%;
  color: rgba(0, 0, 0, 0.75);
  text-justify: auto;
  word-wrap: break-word;
}

article p:last-of-type {
  margin-bottom: 0;
}

h1,
h2,
h3,
h4,
h5 {
  color: rgba(0, 0, 0, 0.8);
  font-family: "Gentium Basic", "Times New Roman", serif;
}

h1, h2 {
  font-size: 1.6rem;
}

a > h1:hover {
  text-decoration: none;
  color: var(--pinkish) !important;
}

img {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  border-radius: 5px;
}

article img {
  max-width: 400px;
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.3);
}

article img:hover {
  box-shadow: 0px 5px 15px #00000040;
}

address {
  display: inline-block;
}

article h1:first-child {
  margin-top: 0;
  margin-bottom: 0;
}

header {
  padding: 0;
}

a, a:visited {
  color: var(--link-color);
  text-decoration: none;
}

a:hover {
  color: var(--pinkish);
}

hr {
  color: #00000020;
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid #00000020;
  margin: 5px 0 0;
  padding: 0;
}

.table--first200 tr>td:first-of-type {
  width: 200px;
}

.table--normal td {
  vertical-align: text-top;
  padding: 0 10px 0 0;
}

.table--normal tr>td:last-child {
  padding-right: 0;
}

@media screen and (max-width: 600px) {
  .table--normal tr>td:last-of-type {
    margin-bottom: 10px;
  }

  .table--normal tr:last-of-type>td:last-of-type {
    margin-bottom: 0;
  }

  .table--normal tr>td {
    width: 100%;
  }

  .table--normal tr>td {
    display: inline-block;
  }
}

.table--scroll table {
  border-collapse: collapse;
  overflow: auto;
  margin: 10px auto 0;
  display: block;
  width: fit-content;
  max-width: 100%;
  box-shadow: 0 0 1px 1px rgba(0, 0, 0, .1);
  border-radius: 5px;
}

.table--scroll tbody>tr:nth-of-type(2n-1) {
  background-color: #50505010;
}

.table--scroll thead>tr {
  background-color: var(--coral);
}

.table--scroll thead th {
  font-weight: bold;
  color: white;
  padding: 5px;
}

.table--scroll tbody>tr:hover {
  background-color: var(--coral-o-cc);
  color: white;
}

.table--scroll td {
  padding: 5px;
}

.nav-btn-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 5px;
}

.reel {
  display: flex;
  margin: 30px 10px;
  justify-content: center;
}

.reel > p {
  display: flex;
  max-width: 100%;
  overflow-x: auto;
  flex-flow: row;
  align-items: center;
  margin: 0;
}

.reel > p > img {
  height: 200px !important;
  width: auto;
  box-shadow: none;
  margin: 0 5px 0 0;
}

::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}

::-webkit-scrollbar-track {
  background: white;
  border: 4px solid transparent;
  background-clip: content-box;
}

::-webkit-scrollbar-thumb {
  background: var(--coral);
  border-radius: 5px;
}

details {
  margin: 1rem 0;
}

summary:hover {
  color: var(--pinkish);
}

summary:focus {
  outline: none;
}
</style>
